import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import noop from 'lib/noop'
import config from 'constants/config'
import FilterSearchInput from 'components/Common/FilterPanel/FilterSearchInput'
import { formToObject } from 'lib/formToObject'
import FilterPanelCheckboxGroup, { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import { EmptyArray, isNonEmptyArray, sortBy, without } from 'lib/arrayUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { OfferListSortOption, SORT_OPTION_RECOMMENDED } from 'constants/offerListFilters'
import RadioButtonInput from 'components/Luxkit/RadioButton/RadioButtonInput'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import usePrevious from 'hooks/usePrevious'
import { CUSTOMER_RATING_GTE, NON_COUNTED_FILTERS } from 'constants/search'
import Group from 'components/utils/Group'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { generateOptionItems, Option, optionMapToOptions } from './OfferListFilterUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import { EnabledFilters, defaultEnabledFilters } from 'components/Search/type'
import { OFFER_TYPE_VILLA } from 'constants/offer'

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${rem(16)};

  > * + * {
    margin-top: ${rem(12)};
  }
`

const ButtonGroup = styled(Group)`
  width: 100%;
  > * {
    flex: 1;
  }
`

const CategoryContainer = styled.div`
  > * + * {
    margin-top: ${rem(24)}
  }
`

const ColourfulLoadingAnimation = styled(LoadingAnimation)`
  margin-top: ${rem(16)};
  color: ${props => props.theme.palette.primary.normal};
`

function toggleSelectedSet(selectedSet: Set<string>, value: string) {
  const nextSelectedSet = new Set([...selectedSet.values()])
  if (nextSelectedSet.has(value)) {
    nextSelectedSet.delete(value)
  } else {
    nextSelectedSet.add(value)
  }
  return nextSelectedSet
}

export type OfferListFilterSubmit = {
  destinations: Array<string>;
  holidayTypes: Array<string>;
  propertyTypes: Array<string>;
  amenities: Array<string>;
  inclusions: Array<string>;
  sortBy: App.OfferListSortByOptions;
  customerRatings: number | undefined;
  offerTypes?: Array<string>;
}

interface Props {
  open?: boolean;
  onClose?: () => void;
  onApply?: (values: OfferListFilterSubmit) => void;
  availableFilters?: App.OfferListAvailableFilters;
  filterOrder?: App.OfferListFilterOrders;
  existingFilters?: App.OfferListFilters;
  sortOptions?: Array<OfferListSortOption>;
  isLoading?: boolean;
  showPropertyTypes?: boolean;
  showHolidayTypes?: boolean;
  enabledFilters?: EnabledFilters;
  showOfferTypes?: boolean;
}

function OfferListFilterSidebar(props: Props) {
  const {
    open,
    onClose,
    availableFilters,
    filterOrder,
    existingFilters,
    onApply,
    sortOptions = EmptyArray,
    isLoading,
    showPropertyTypes,
    showHolidayTypes,
    enabledFilters = defaultEnabledFilters,
    showOfferTypes = false,
  } = props

  const [destinationSearch, setDestinationSearch] = useState<string>()
  const [localDestinations, setLocalDestinations] = useState<Set<string>>(new Set())
  const [localHolidayTypes, setLocalHolidayTypes] = useState<Set<string>>(new Set())
  const [localCustomerRating, setLocalCustomerRating] = useState<number>()
  const [localPropertyTypes, setLocalPropertyTypes] = useState<Set<string>>(new Set())
  const [localAmenities, setLocalAmenities] = useState<Set<string>>(new Set())
  const [localInclusions, setLocalInclusions] = useState<Set<string>>(new Set())
  const [localSort, setLocalSort] = useState<App.OfferListSortByOptions>()
  const [localOfferTypes, setLocalOfferTypes] = useState<Set<string>>(new Set())
  const previouslyOpen = usePrevious(open, false)

  useEffect(() => {
    if (!previouslyOpen && open) {
      // just opened, reset based on existing filters
      setLocalDestinations(new Set(existingFilters?.locations))
      setLocalHolidayTypes(new Set(existingFilters?.holidayTypes))
      setLocalCustomerRating(existingFilters?.customerRatingGte)
      setLocalPropertyTypes(new Set(existingFilters?.propertyTypes))
      setLocalAmenities(new Set(existingFilters?.amenities))
      setLocalInclusions(new Set(existingFilters?.inclusions))
      setLocalSort(existingFilters?.sortBy ?? SORT_OPTION_RECOMMENDED.value)
      const offerTypes: Array<string> = []

      if (existingFilters?.offerTypes && existingFilters.offerTypes.includes(OFFER_TYPE_VILLA)) {
        offerTypes.push(OFFER_TYPE_VILLA)
      }
      if (existingFilters?.offerTypes && existingFilters.offerTypes.length > 1) {
        offerTypes.push('hotel')
      }
      setLocalOfferTypes(new Set(offerTypes))
    }
  }, [existingFilters, open, previouslyOpen])

  const onFilterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const values = formToObject<OfferListFilterSubmit>(e.currentTarget)
    if (onApply) {
      onApply({
        holidayTypes: values.holidayTypes,
        propertyTypes: values.propertyTypes,
        destinations: values.destinations,
        customerRatings: values.customerRatings,
        amenities: values.amenities,
        inclusions: values.inclusions,
        sortBy: values.sortBy,
        offerTypes: values.offerTypes,
      })
    }
  }

  const [
    destinations,
    holidayTypes,
    amenities,
    inclusions,
    offerTypes,
  ] = useMemo(() => {
    const types = {
      hotel: 0,
      [OFFER_TYPE_VILLA]: 0,
    }
    return [
      optionMapToOptions(availableFilters?.locations ?? {}, filterOrder?.locations ?? {}),
      optionMapToOptions(availableFilters?.holidayTypes ?? {}, filterOrder?.holidayTypes ?? {}),
      optionMapToOptions(availableFilters?.amenities ?? {}, filterOrder?.amenities ?? {}),
      Object.entries(availableFilters?.inclusions ?? {}).reduce((acc, [category, inclusions]) => {
        acc[category] = optionMapToOptions(inclusions ?? {}, {})
        return acc
      }, {} as Record<string, Option[]>),
      optionMapToOptions(types, {}),
    ] }, [availableFilters, filterOrder])

  const onDestinationSearchChange = useCallback((val: string) => {
    setDestinationSearch(val)
  }, [])

  const onDestinationChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextDestinations = toggleSelectedSet(localDestinations, e.target.value)
    setLocalDestinations(nextDestinations)
  }, [localDestinations])

  const onDestinationReset = useCallback(() => {
    setLocalDestinations(new Set())
  }, [])

  const onHolidayTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextHolidayTypes = toggleSelectedSet(localHolidayTypes, e.target.value)
    setLocalHolidayTypes(nextHolidayTypes)
  }, [localHolidayTypes])

  const onHolidayTypeReset = useCallback(() => {
    setLocalHolidayTypes(new Set())
  }, [])

  const onCustomerRatingChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (localCustomerRating === Number(value)) {
      setLocalCustomerRating(undefined)
    } else {
      setLocalCustomerRating(Number(value))
    }
  }, [localCustomerRating])

  const onCustomerRatingReset = useCallback(() => {
    setLocalCustomerRating(undefined)
  }, [])

  const onPropertyTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextPropertyTypes = toggleSelectedSet(localPropertyTypes, e.target.value)
    setLocalPropertyTypes(nextPropertyTypes)
  }, [localPropertyTypes])

  const onPropertyTypeReset = useCallback(() => {
    setLocalPropertyTypes(new Set())
  }, [])

  const onAmenitiesChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextAmenities = toggleSelectedSet(localAmenities, e.target.value)
    setLocalAmenities(nextAmenities)
  }, [localAmenities])

  const onAmenitiesReset = useCallback(() => {
    setLocalAmenities(new Set())
  }, [])

  const onInclusionsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextInclusions = toggleSelectedSet(localInclusions, e.target.value)
    setLocalInclusions(nextInclusions)
  }, [localInclusions])

  const onInclusionsReset = useCallback(() => {
    setLocalInclusions(new Set())
  }, [])

  const onOfferTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextOfferTypes = toggleSelectedSet(localOfferTypes, e.target.value)
    if (nextOfferTypes.size !== 0) {
      setLocalOfferTypes(nextOfferTypes)
    }
  }, [localOfferTypes])

  const onOfferTypeReset = useCallback(() => {
    setLocalOfferTypes(new Set(['hotel', 'villa']))
  }, [])

  const onFormReset = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (onApply) {
      onApply({
        holidayTypes: [],
        propertyTypes: [],
        destinations: [],
        amenities: [],
        inclusions: [],
        sortBy: 'recommended',
        customerRatings: undefined,
        offerTypes: ['hotel', 'villa'],
      }) }
  }, [onApply])

  const destinationItems = useMemo(() => {
    return sortBy(destinations, (dest) => dest[0], 'asc').map(([destination, count]) => ({
      label: <Group direction="horizontal" horizontalAlign="space-between">
        {destination}
        <span>({count})</span>
      </Group>,
      value: destination,
      checked: localDestinations.has(destination),
      hidden: !!destinationSearch && !destination.toLowerCase().includes(destinationSearch.toLowerCase()),
    }))
  }, [destinations, destinationSearch, localDestinations])

  const holidayTypeItems = useMemo(
    () => generateOptionItems(holidayTypes, localHolidayTypes, true),
    [holidayTypes, localHolidayTypes],
  )

  const propertyTypeItems = useMemo(
    () => showPropertyTypes ? [
      { value: 'UNIQUE_STAYS', label: 'Villas & Unique Stays', checked: localPropertyTypes.has('UNIQUE_STAYS') },
      { value: 'HOTEL', label: 'Hotels & Resorts', checked: localPropertyTypes.has('HOTEL') },
    ] : [],
    [showPropertyTypes, localPropertyTypes],
  )

  const showFilterByCustomerRating = config.SEARCH_FILTER_BY_CUSTOMER_RATING_ENABLED

  const customerRatingItems = useMemo(
    () => {
      if (!showFilterByCustomerRating) {
        return []
      }
      const items = [
        {
          value: CUSTOMER_RATING_GTE.EXCEPTIONAL.value,
          label: <Group direction="horizontal" horizontalAlign="space-between">
            {CUSTOMER_RATING_GTE.EXCEPTIONAL.label}
          </Group>,
          checked: localCustomerRating === CUSTOMER_RATING_GTE.EXCEPTIONAL.value,
          count: availableFilters?.customerRatings?.Exceptional,
        },
        {
          value: CUSTOMER_RATING_GTE.VERY_GOOD.value,
          label: <Group direction="horizontal" horizontalAlign="space-between">
            {CUSTOMER_RATING_GTE.VERY_GOOD.label}
          </Group>,
          checked: localCustomerRating === CUSTOMER_RATING_GTE.VERY_GOOD.value,
          count: availableFilters?.customerRatings?.['Very Good'],
        },
        {
          value: CUSTOMER_RATING_GTE.GOOD.value,
          label: <Group direction="horizontal" horizontalAlign="space-between">
            {CUSTOMER_RATING_GTE.GOOD.label}
          </Group>,
          checked: localCustomerRating === CUSTOMER_RATING_GTE.GOOD.value,
          count: availableFilters?.customerRatings?.Good,
        },
      ]
      return items.filter((item) => item.count !== 0)
    }, [availableFilters?.customerRatings, localCustomerRating, showFilterByCustomerRating])

  const amenitiesItems = useMemo(
    () => generateOptionItems(amenities, localAmenities, true),
    [amenities, localAmenities],
  )

  const inclusionsItems = useMemo(
    () => Object.entries(inclusions).reduce((acc, [category, inclusionOptions]) => {
      acc[category] = generateOptionItems(inclusionOptions, localInclusions, true)
      return acc
    }, {} as Record<string, FilterPanelCheckItem[]>),
    [inclusions, localInclusions],
  )

  const offerTypesItems = useMemo(() => generateOptionItems(offerTypes, localOfferTypes, true), [localOfferTypes, offerTypes])

  const noFilterAvailable = !isLoading && Math.max(
    destinations.length,
    holidayTypes.length,
    propertyTypeItems.length,
    amenities.length,
    Object.keys(inclusions).length,
  ) == 0

  const sortAndFiltersText = isNonEmptyArray(sortOptions) ? 'Sort & filter' : 'Filters'

  const filterCount = (without(existingFilters?.amenities ?? [], ...NON_COUNTED_FILTERS.amenities).length) +
    (without(existingFilters?.locations ?? [], ...NON_COUNTED_FILTERS.locations).length ?? 0) +
    (without(existingFilters?.holidayTypes ?? [], ...NON_COUNTED_FILTERS.holidayTypes).length)

  const customerRatingsAvailable = customerRatingItems.length > 0 && customerRatingItems.every(customerRating => customerRating.count !== undefined)
  const resetAllText = `Reset all ${filterCount > 0 ? `(${filterCount})` : ''}`

  return (
    <ModalBase
      mode="drawer"
      isOpen={open}
      onClose={onClose}
    >
      <form onSubmit={onFilterSubmit} onReset={onFormReset}>
        <ModalHeader
          title={sortAndFiltersText}
          onCloseButtonClick={onClose}
        />
        <ModalBody>
          <ModalContent>
            {sortOptions.length > 0 && <CSSBreakpoint max="mobile">
              <FilterPanelFilterGroup title="Sort by">
                <InputGroup>
                  {sortOptions.map(sortOption => <RadioButtonInput
                    key={`sortOption_${sortOption.value}`}
                    name="sortBy"
                    value={sortOption.value}
                    onChange={() => setLocalSort(sortOption.value)}
                    checked={sortOption.value === localSort}
                  >
                    {sortOption.label}
                  </RadioButtonInput>)}
                </InputGroup>
              </FilterPanelFilterGroup>
            </CSSBreakpoint>}
            {isLoading && <CSSBreakpoint min="tablet">
              <ColourfulLoadingAnimation size={8} />
            </CSSBreakpoint>}
            {noFilterAvailable && <CSSBreakpoint min="tablet">
              <BodyText variant="large">
                No filters are available for your search.
              </BodyText>
            </CSSBreakpoint>}
            {enabledFilters.locations && destinations.length > 0 && <FilterPanelFilterGroup
                title="Destination"
                canReset={localDestinations.size > 0}
                onReset={onDestinationReset}
              >
              {destinations.length > 10 &&
                <InputGroup>
                  <FilterSearchInput
                    placeholder="Search destinations"
                    onChange={onDestinationSearchChange}
                    name="destinationSearch"
                  />
                  <FilterPanelCheckboxGroup
                    name="destinations"
                    items={destinationItems}
                    onChange={onDestinationChange}
                    maxItems={10}
                  />
                </InputGroup>}
              {destinations.length <= 10 && <FilterPanelCheckboxGroup
                name="destinations"
                items={destinationItems}
                onChange={onDestinationChange}
                maxItems={10}
              />}
            </FilterPanelFilterGroup>}
            {propertyTypeItems.length > 0 && <FilterPanelFilterGroup
                title="Category"
                canReset={localPropertyTypes.size > 0}
                onReset={onPropertyTypeReset}
              >
              <FilterPanelCheckboxGroup
                name="propertyTypes"
                items={propertyTypeItems}
                onChange={onPropertyTypeChange}
                maxItems={10}
              />
            </FilterPanelFilterGroup>}
            {enabledFilters.holidayTypes && holidayTypes.length > 0 && showHolidayTypes && <FilterPanelFilterGroup
              title={`Type of ${config.brandConfig.holidaySynonym}`}
              canReset={localHolidayTypes.size > 0}
              onReset={onHolidayTypeReset}
            >
              <FilterPanelCheckboxGroup
                name="holidayTypes"
                items={holidayTypeItems}
                onChange={onHolidayTypeChange}
                maxItems={10}
              />
            </FilterPanelFilterGroup>}
            {enabledFilters.customerRating && customerRatingsAvailable && showFilterByCustomerRating && <FilterPanelFilterGroup
              title="Customer rating"
              canReset={!!localCustomerRating}
              onReset={onCustomerRatingReset}
            >
              <FilterPanelCheckboxGroup
                name="customerRatings"
                items={customerRatingItems}
                onChange={onCustomerRatingChange}
                maxItems={3}
              />
            </FilterPanelFilterGroup>}
            {enabledFilters.amenities && amenities.length > 0 && <FilterPanelFilterGroup
              title="Amenities"
              canReset={localAmenities.size > 0}
              onReset={onAmenitiesReset}
            >
              <FilterPanelCheckboxGroup
                name="amenities"
                items={amenitiesItems}
                onChange={onAmenitiesChange}
                maxItems={10}
              />
            </FilterPanelFilterGroup>}

            {enabledFilters.inclusions && Object.keys(inclusions).length > 0 && <FilterPanelFilterGroup
              title="Inclusions"
              canReset={localInclusions.size > 0}
              onReset={onInclusionsReset}
            >
              <CategoryContainer>
                {Object.entries(inclusionsItems).map(([category, items]) => (
                  <div key={category}>
                    <BodyText variant="medium" weight="bold">{category}</BodyText>
                    <FilterPanelCheckboxGroup
                      key={category}
                      name="inclusions"
                      items={items}
                      onChange={onInclusionsChange}
                      maxItems={10}
                    />
                  </div>
                ))}
              </CategoryContainer>
            </FilterPanelFilterGroup>}
            {enabledFilters.offerTypes && showOfferTypes && Object.keys(offerTypes).length > 0 && <FilterPanelFilterGroup
              title="Property Type"
              canReset={localOfferTypes.size < 2}
              onReset={onOfferTypeReset}
            >
              <FilterPanelCheckboxGroup
                  name="offerTypes"
                  items={offerTypesItems}
                  onChange={onOfferTypeChange}
                  titlecase
                />
            </FilterPanelFilterGroup>}

          </ModalContent>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup direction="horizontal" gap={12}>
            <TextButton
              kind="tertiary"
              size="large"
              type="reset"
            >
              {resetAllText}
            </TextButton>
            <TextButton
              kind="primary"
              type="submit"
              size="large"
            >
              View offers
            </TextButton>
          </ButtonGroup>
        </ModalFooter>
      </form>
    </ModalBase>
  )
}

OfferListFilterSidebar.defaultProps = {
  onApply: noop,
  sortOptions: [],
}

export default OfferListFilterSidebar
